import React, { useState } from 'react';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button';
import { nodeTemplateService } from '../../services/nodeTemplateService';
import { useNotificationStore } from '../../stores/notificationStore';

interface SaveAsTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  canvasId: string;
  nodeId: string;
  onSuccess?: () => void;
}

export function SaveAsTemplateModal({
  isOpen,
  onClose,
  canvasId,
  nodeId,
  onSuccess,
}: SaveAsTemplateModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notify = useNotificationStore();

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await nodeTemplateService.create({
        name: name.trim(),
        description: description.trim() || undefined,
        source_canvas_id: canvasId,
        source_node_id: nodeId,
      });

      notify.success('Template created successfully');
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      const message = err.message || 'Failed to create template';
      setError(message);
      notify.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Save as Template"
      description="Create a reusable template from this Generate node. The template will reference the live state of this node."
      size="sm"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">
            Template Name *
          </label>
          <input
            id="template-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            placeholder="e.g., OG Image Generator"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        </div>

        <div>
          <label htmlFor="template-description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="template-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of what this template does..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
            {error}
          </div>
        )}

        <div className="bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2">
          <p className="text-xs text-indigo-700">
            <strong>Note:</strong> This node will be protected from deletion while used as a template source.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          isLoading={isSaving}
          disabled={!name.trim()}
        >
          Create Template
        </Button>
      </div>
    </Modal>
  );
}
